import { apiFiles } from "@/api/file.api.ts";
import { DatepickerRange } from "@/components/datepicker/DatepickerRange.tsx";
import { getEmojiColorPalette } from "@/components/emoji/emoji-colors.ts";
import { getUIKitColorHSLA } from "@/helpers/style.helper.ts";
import { FileItem } from "@/components/files/FileItem.tsx";
import { FileUpload } from "@/components/files/FileUpload.tsx";
import { GradientContainer } from "@/components/gradient-container/GradientContainer.tsx";
import {
  parseUrlHelper,
  Avatar,
  Box,
  Button,
  DotSeparator,
  EmojiDisplay,
  FullScreenPage,
  LinkButtonTo,
  ListItem,
  Radio,
  Skeleton,
  Stack,
  Tag,
  Text,
  Textarea,
  Title,
  type DatepickerRangeValue,
  toCssColorValue,
  useModals,
} from "@skailer-com/uikit";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { TimeOffRequestRejectInfo } from "@/pages/time-off/components/TimeOffRequestRejectInfo.tsx";
import { TimeOffRequestFinal } from "@/pages/time-off/edit/components/TimeOffRequestFinal.tsx";
import { TimeOffRequestLoading } from "@/pages/time-off/edit/components/TimeOffRequestLoading.tsx";
import { TimeOffBreakdownModal } from "@/pages/time-off/widgets/TimeOffBreakdownModal.tsx";
import {
  buildEmployeeTimeOffDayInfoMap,
  EmployeeTimeOffDayInfo,
  getEmployeeTimeOffDayKey,
} from "@/pages/employees/employee/components/employee-time-off-calendar.helper.ts";
import {
  useEmployeeDetails,
  useEmployeeMe,
  useEmployeeTimeOffCalendar,
  useEmployeeTimeOffCategoriesData,
} from "@/queries/employees/use-employees.query.ts";
import {
  useMyTimeOffRequestDetails,
  useTimeOffRequestCreate,
  useTimeOffRequestUpdate,
} from "@/queries/use-time-off-requests.query.ts";
import { useTimeOffCheck } from "@/queries/use-time-off.query.ts";

import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiErrorException, ApiValidationError } from "@/types/api.types.ts";
import { ApiApprovalRequestStatus } from "@/types/approvals/approval.types.ts";
import { ApiCalendarHolidayDateType } from "@/types/calendar-holiday.types.ts";
import { ApiEntityTypes, ApiFile } from "@/types/common.types.ts";
import {
  ApiEmployeeShort,
  ApiEmployeeTimeOffCategoryPayload,
} from "@/types/employees/employee.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import {
  ApiTimeOffCategoryType,
  ApiTimeOffPeriodTrackingTime,
} from "@/types/time-off/time-off-policy.types.ts";
import {
  ApiEmployeeTimeOffCategory,
  ApiTimeOffBreakdown,
  ApiTimeOffLogFull,
  ApiTimeOffLogFullRejectCode,
  ApiTimeOffRequestSave,
} from "@/types/time-off/time-off.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { addMonths, isToday } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { z } from "zod";

enum TimeOffRequestEditStep {
  form = "form",
  final = "final",
}

// eslint-disable-next-line react-refresh/only-export-components
export const buildTimeOffRequestSchema = (requiredMessage: string) =>
  z.object({
    selectedCategoryId: z.number({ error: requiredMessage }),
    periodStart: z.date({ error: requiredMessage }),
    periodEnd: z.date({ error: requiredMessage }),
    note: z.string().nullish(),
    approversList: z.array(z.custom<ApiEmployeeShort>()).nullish(),
    createAsAdmin: z.boolean().nullish(),
  });

interface TimeOffPeriodPickerProps {
  dateFrom?: Date;
  dateTo?: Date;
  employeeId?: number;
  error?: string;
  excludeRequestId?: number;
  label: string;
  onChange: (dateFrom: Date, dateTo: Date) => void;
}

const TimeOffPeriodPicker = ({
  dateFrom,
  dateTo,
  employeeId,
  error,
  excludeRequestId,
  label,
  onChange,
}: TimeOffPeriodPickerProps) => {
  const { t } = useTranslation();
  const [visibleMonth, setVisibleMonth] = useState(dateFrom ?? new Date());
  const visibleNextMonth = addMonths(visibleMonth, 1);
  const visibleYear = visibleMonth.getFullYear();
  const visibleNextYear = visibleNextMonth.getFullYear();
  const { data: calendar } = useEmployeeTimeOffCalendar(employeeId ?? 0, visibleYear, !!employeeId);
  const { data: nextYearCalendar } = useEmployeeTimeOffCalendar(
    employeeId ?? 0,
    visibleNextYear,
    !!employeeId && visibleNextYear !== visibleYear
  );
  const dayInfoMap = useMemo(
    () =>
      buildEmployeeTimeOffDayInfoMap(
        visibleNextYear === visibleYear ? [calendar] : [calendar, nextYearCalendar],
        excludeRequestId
      ),
    [calendar, excludeRequestId, nextYearCalendar, visibleNextYear, visibleYear]
  );

  const renderDayTooltip = useCallback(
    ({ holidays, requests }: EmployeeTimeOffDayInfo) => (
      <Stack gap="xs" className="max-w-64">
        {holidays.map((holiday, index) => (
          <Stack key={`holiday-${index}`} direction="horizontal" gap="xs" items="center">
            {holiday.type !== ApiCalendarHolidayDateType.working && (
              <EmojiDisplay id=":tada:" size="xs" />
            )}
            <Text className="text-xs">{holiday.title}</Text>
          </Stack>
        ))}
        {requests.map((request) => (
          <Stack key={request.id} direction="horizontal" gap="xs" items="center">
            {request.time_off_data.category.emoji && (
              <EmojiDisplay id={request.time_off_data.category.emoji} size="xs" />
            )}
            <Text className="text-xs">
              {request.status === ApiApprovalRequestStatus.awaiting && `${t("common:request")}: `}
              {request.time_off_data.category.title}
            </Text>
          </Stack>
        ))}
      </Stack>
    ),
    [t]
  );

  const getDayDecoration = useCallback(
    (date: Date, { isRangeEnd, isRangeStart }: { isRangeEnd: boolean; isRangeStart: boolean }) => {
      const dayInfo = dayInfoMap.get(getEmployeeTimeOffDayKey(date)) ?? {
        holidays: [],
        requests: [],
      };

      const requests = dayInfo.requests.filter((request) =>
        [ApiApprovalRequestStatus.approved, ApiApprovalRequestStatus.awaiting].includes(
          request.status
        )
      );
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      const isWorkDay = dayInfo.holidays.some(
        (holiday) => holiday.type === ApiCalendarHolidayDateType.working
      );
      const hasHoliday = dayInfo.holidays.length > 0;
      const request = requests[0];
      const requestIsApproved = request?.status === ApiApprovalRequestStatus.approved;
      const categoryColor = request?.time_off_data.category.color;
      const hasTooltip = hasHoliday || requests.length > 0;

      if (!hasTooltip && !isWeekend && !isToday(date)) return undefined;

      return {
        className: twMerge(
          (hasHoliday || isWeekend) && !isWorkDay && "text-secondary",
          requestIsApproved && "text-light",
          (isRangeStart || isRangeEnd) && "text-light"
        ),
        content: (
          <>
            {(hasHoliday || isWeekend) && !isWorkDay && (
              <span className="pointer-events-none absolute inset-0.5 rounded-xs bg-danger/10" />
            )}
            {isToday(date) && (
              <span className="pointer-events-none absolute inset-0.5 rounded-xs border-2 border-dark" />
            )}
            {request && (
              <span
                className={twMerge(
                  "pointer-events-none absolute inset-0.5 rounded-xs",
                  !requestIsApproved && "border-2 border-dashed"
                )}
                style={{
                  backgroundColor: requestIsApproved
                    ? toCssColorValue(categoryColor, getUIKitColorHSLA("brand-dark", 0.2))
                    : undefined,
                  borderColor: !requestIsApproved
                    ? toCssColorValue(categoryColor, getUIKitColorHSLA("brand-dark", 0.2))
                    : undefined,
                }}
              />
            )}
          </>
        ),
        tooltip: hasTooltip
          ? renderDayTooltip({ holidays: dayInfo.holidays, requests })
          : undefined,
      };
    },
    [dayInfoMap, renderDayTooltip]
  );

  const handleChange = (nextRange: DatepickerRangeValue | undefined) => {
    if (nextRange?.from && nextRange.to) {
      onChange(nextRange.from, nextRange.to);
    }
  };

  return (
    <DatepickerRange
      value={dateFrom ? { from: dateFrom, to: dateTo } : undefined}
      onChange={handleChange}
      changeOnClose
      label={label}
      error={error}
      openToDate={dateFrom}
      onMonthChange={setVisibleMonth}
      getDayDecoration={getDayDecoration}
    />
  );
};

export const TimeOffRequestEdit = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const timeOffId = id ? Number(id) : 0;
  const [searchParams, setSearchParams] = useSearchParams();
  const employeeId = parseUrlHelper.getNumber(searchParams, "employeeId");
  const categoryId = parseUrlHelper.getNumber(searchParams, "categoryId");
  const startDate = searchParams.get("startDate");
  const { data: timeOffRequest, isLoading: timeOffRequestLoading } =
    useMyTimeOffRequestDetails(timeOffId);
  const { renderDate, renderEmployeeName } = useFormatter();
  const { openModal } = useModals();
  const { handleErrors } = useErrorHandle();
  const { canIEdit, isFeatureEnabled } = usePermissions();
  const { data: employeeMe } = useEmployeeMe();
  const isEditMode = !!timeOffRequest;
  const [editedFiles, setFiles] = useState<ApiFile[]>();
  const files = useMemo(
    () => editedFiles ?? timeOffRequest?.time_off_log?.files ?? [],
    [editedFiles, timeOffRequest?.time_off_log?.files]
  );
  const targetEmployeeId = employeeId ?? timeOffRequest?.time_off_log.employee.id ?? employeeMe?.id;
  const [step, setStep] = useState<TimeOffRequestEditStep>(TimeOffRequestEditStep.form);
  const [requestSaved, setRequestSaved] = useState<ApiTimeOffLogFull | undefined>();
  const [breakdown, setBreakdown] = useState<ApiTimeOffBreakdown | undefined>(
    timeOffRequest?.time_off_log?.breakdown
  );
  const [payloadCategory, setPayloadCategory] = useState<ApiEmployeeTimeOffCategoryPayload>({
    date_balance: new Date(),
  });
  const { data: targetEmployee, isPending: isTargetEmployeeLoading } =
    useEmployeeDetails(targetEmployeeId);

  useTitle(
    isEditMode ? t("time_off:edit_timeoff_request") : t("time_off:create_new_timeoff_request")
  );

  useEffect(() => {
    if (timeOffRequest?.time_off_log?.breakdown) {
      setBreakdown(timeOffRequest.time_off_log.breakdown);
    }
  }, [timeOffRequest]);

  const { data: employeeTimeOffCategories, isLoading: employeeTimeOffCategoriesIsLoading } =
    useEmployeeTimeOffCategoriesData(targetEmployeeId ?? 0, payloadCategory, !!targetEmployeeId);

  const timeOffSchema = buildTimeOffRequestSchema(t("errors:field_required"));

  const {
    register,
    setValue,
    reset,
    handleSubmit,
    watch,
    control,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(timeOffSchema),
    defaultValues: timeOffRequest?.time_off_log
      ? {
          selectedCategoryId: timeOffRequest.time_off_log.category.id,
          periodStart: timeOffRequest.time_off_log.date_from
            ? new Date(timeOffRequest.time_off_log.date_from)
            : undefined,
          periodEnd: timeOffRequest.time_off_log.date_to
            ? new Date(timeOffRequest.time_off_log.date_to)
            : undefined,
          note: timeOffRequest.time_off_log.comment || "",
          approversList: [],
          createAsAdmin: false,
        }
      : {
          selectedCategoryId: categoryId,
          periodStart: startDate ? new Date(startDate) : undefined,
          periodEnd: startDate ? new Date(startDate) : undefined,
          note: "",
          approversList: [],
          createAsAdmin: false,
        },
  });

  const timeOffRequestCreate = useTimeOffRequestCreate(control);
  const timeOffRequestUpdate = useTimeOffRequestUpdate(control);

  const selectedCategoryId = watch("selectedCategoryId");
  const periodStart = watch("periodStart");
  const periodEnd = watch("periodEnd");
  const approvers = watch("approversList") || [];
  const createAsAdmin = watch("createAsAdmin");

  useEffect(() => {
    if (periodStart) {
      setPayloadCategory((prev) => ({
        ...prev,
        date_balance: periodStart,
        exclude_request_id: timeOffRequest?.approval_request_details?.id,
      }));
    }
  }, [periodStart, timeOffRequest?.approval_request_details?.id]);

  const checkPayload = useMemo((): ApiTimeOffRequestSave | undefined => {
    if (timeOffId > 0 && !timeOffRequest) return undefined;
    if (!selectedCategoryId || !periodStart || !periodEnd) return undefined;

    return {
      request_id: timeOffRequest?.approval_request_details?.id ?? null,
      file_uuids: files.map((file) => file.uuid),
      time_off_data: {
        category_id: selectedCategoryId,
        date_from: periodStart,
        date_to: periodEnd,
      },
      employee_id: targetEmployeeId,
      is_bypass_approval_flow: createAsAdmin || false,
      breakdown: breakdown?.is_manual_modified ? breakdown : undefined,
    };
  }, [
    files,
    timeOffId,
    timeOffRequest,
    targetEmployeeId,
    selectedCategoryId,
    periodStart,
    periodEnd,
    createAsAdmin,
    breakdown?.is_manual_modified,
    breakdown,
  ]);

  const {
    data: timeOffCheck,
    isLoading: timeOffCheckIsLoading,
    isFetching: timeOffCheckIsFetching,
    error: employeeTimeOffCheckError,
  } = useTimeOffCheck(checkPayload);

  const hasApprovalPolicyError = !!(
    (employeeTimeOffCheckError as ApiErrorException)?.apiError as ApiValidationError
  )?.validationErrors?.some((err) => err.code === "approval_policy_no_confirming_employees");

  useEffect(() => {
    if (!employeeTimeOffCategories?.length) return;

    const hasCurrentCategory = employeeTimeOffCategories.some(
      (category) => category.id === selectedCategoryId
    );

    if (!hasCurrentCategory) {
      setValue("selectedCategoryId", employeeTimeOffCategories[0].id);
    }
  }, [employeeTimeOffCategories, selectedCategoryId, setValue]);

  useEffect(() => {
    if (!timeOffRequest) return;

    reset({
      selectedCategoryId: timeOffRequest.time_off_log.category.id,
      periodStart: timeOffRequest.time_off_log.date_from
        ? new Date(timeOffRequest.time_off_log.date_from)
        : undefined,
      periodEnd: timeOffRequest.time_off_log.date_to
        ? new Date(timeOffRequest.time_off_log.date_to)
        : undefined,
      note: timeOffRequest.time_off_log.comment || "",
      approversList: [],
      createAsAdmin: false,
    });
  }, [timeOffRequest]);

  useEffect(() => {
    if (timeOffCheck?.confirming_employees) {
      setValue("approversList", timeOffCheck.confirming_employees);
    }
    if (timeOffCheck?.breakdown && !breakdown?.is_manual_modified) {
      setBreakdown(timeOffCheck.breakdown);
    }
  }, [timeOffCheck]);

  useEffect(() => {
    setBreakdown((prev) => (prev ? { ...prev, is_manual_modified: false } : undefined));
  }, [selectedCategoryId, periodStart, periodEnd]);

  const selectedCategory = employeeTimeOffCategories?.find(
    (category: ApiEmployeeTimeOffCategory) => category.id === selectedCategoryId
  );

  const handleCustomizeBreakdown = () => {
    if (!breakdown) return;
    openModal(TimeOffBreakdownModal, {
      breakdown,
      period_track_type: selectedCategory?.period_track_type,
      available_balance: Math.round(Number(selectedCategory?.balance.available_balance)) || 0,
      onApply: (updatedBreakdown: ApiTimeOffBreakdown) => {
        setBreakdown(updatedBreakdown);
      },
    });
  };

  const onSubmit = handleSubmit((formData) => {
    if (
      timeOffCheckIsFetching ||
      timeOffCheck?.reject_code === ApiTimeOffLogFullRejectCode.attachment_required
    )
      return;

    const payload: ApiTimeOffRequestSave = {
      comment: formData.note || undefined,
      time_off_data: {
        category_id: formData.selectedCategoryId,
        date_from: formData.periodStart,
        date_to: formData.periodEnd,
      },
      file_uuids: files.map((file) => file.uuid),
      employee_id: targetEmployeeId,
      is_bypass_approval_flow: formData.createAsAdmin || false,
      breakdown: breakdown,
    };

    if (isEditMode) {
      if (!timeOffRequest?.approval_request_details) {
        return;
      }
      timeOffRequestUpdate.mutate(
        { id: timeOffRequest?.approval_request_details.id, payload },
        {
          onSuccess: (updatedRequest) => {
            analyticsService.trackEvent(analyticEvents.approvals.requestEdited, {
              [analyticProperties.id]: updatedRequest.approval_request_details?.id,
              [analyticProperties.title]: "Time Off",
            });

            setRequestSaved(updatedRequest);
            setStep(TimeOffRequestEditStep.final);
          },
        }
      );
    } else {
      timeOffRequestCreate.mutate(payload, {
        onSuccess: (createdRequest) => {
          analyticsService.trackEvent(analyticEvents.approvals.requestCreated, {
            [analyticProperties.id]: createdRequest.approval_request_details?.id,
            [analyticProperties.title]: "Time Off",
          });

          history.replaceState(
            null,
            "",
            `/time-off/request/${createdRequest.approval_request_details?.id}/edit`
          );
          setRequestSaved(createdRequest);
          setStep(TimeOffRequestEditStep.final);
        },
      });
    }
  });

  const handleSelectCategory = (id: number) => {
    setValue("selectedCategoryId", id);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("categoryId", String(id));
        return next;
      },
      { replace: true }
    );
  };

  const emojiColors = useMemo(() => {
    if (selectedCategory?.emoji) {
      return getEmojiColorPalette(selectedCategory.emoji);
    }
    if (categoryId && employeeTimeOffCategories?.length) {
      const category = employeeTimeOffCategories.find((c) => c.id === categoryId);
      if (category?.emoji) {
        return getEmojiColorPalette(category.emoji);
      }
    }
    return [];
  }, [selectedCategory, categoryId, employeeTimeOffCategories]);

  async function removeFile(file: ApiFile) {
    setFiles(files.filter((f) => f.id !== file.id));
    const { error } = await apiFiles.delete(file.uuid);
    if (error) handleErrors(error);
  }

  const timeOffDays =
    periodStart && periodEnd && selectedCategory ? (timeOffCheck?.balance_requested ?? 0) : 0;

  const canShowBypassOption =
    isFeatureEnabled(ApiFeatureType.approvals) && canIEdit(ApiPermission.approve_and_reject_leaves);

  const closePage = () => {
    if (new RegExp("time-off", "gi").test(document.referrer) && history.length == 1) {
      window.close();
    } else {
      if (history.length > 2) {
        navigate(-1);
      } else {
        navigate(`/employees/${targetEmployeeId}/time-off-history`);
      }
    }
  };

  const isHours = selectedCategory?.period_track_type === ApiTimeOffPeriodTrackingTime.hours;
  const pluralKey = isHours ? "plurals:plural_hours" : "plurals:plural_day";
  const isFormLoading = employeeTimeOffCategoriesIsLoading || timeOffCheckIsFetching;
  const isAttachmentMissing =
    timeOffCheck?.reject_code === ApiTimeOffLogFullRejectCode.attachment_required;

  return (
    <>
      {timeOffId > 0 && timeOffRequestLoading ? <TimeOffRequestLoading /> : <></>}
      {step == "form" && !timeOffRequestLoading && (
        <FullScreenPage
          isCenterVertical={false}
          titleSlot={
            <DotSeparator className={"text-title text-lg"}>
              {isEditMode
                ? t("time_off:edit_timeoff_request")
                : t("time_off:create_new_timeoff_request")}
              <Text>
                {isTargetEmployeeLoading
                  ? "..."
                  : targetEmployee
                    ? renderEmployeeName(targetEmployee)
                    : ""}
              </Text>
            </DotSeparator>
          }
          backSlot={
            <Button size={"lg"} variant={"secondary"} onClick={closePage}>
              {t("common:exit")}
            </Button>
          }
          actionsSlot={
            <Button
              className={
                timeOffCheck &&
                !timeOffCheck.possible_to_create &&
                !canIEdit(ApiPermission.approve_and_reject_leaves)
                  ? "opacity-50 pointer-events-none"
                  : ""
              }
              type={"submit"}
              onClick={onSubmit}
              size={"lg"}
              disabled={isFormLoading || isAttachmentMissing}
              isLoading={timeOffRequestUpdate.isPending || timeOffRequestCreate.isPending}
            >
              {isEditMode ? t("common:save") : t("common:submit_the_request")}
            </Button>
          }
          backgroundSlot={
            <div className="absolute inset-0 [[data-color-scheme=dark]_&]:opacity-10">
              <GradientContainer colorPalette={emojiColors} addLightOverlayToBottom={true} />
            </div>
          }
        >
          <form onSubmit={onSubmit} className={"gap-sm flex flex-col"}>
            <Stack gap={"sm"}>
              <Stack className={"card-panel bg-light/80 backdrop-blur-md"} gap={"2xl"}>
                <Box className={"relative w-41 h-41"}>
                  {selectedCategory?.emoji && (
                    <Avatar
                      variant={"opaque"}
                      url={undefined}
                      emoji={selectedCategory.emoji}
                      size={"2xl"}
                    />
                  )}
                </Box>
                <Title
                  size={"lg"}
                  header={
                    isEditMode
                      ? t("time_off:edit_timeoff_request")
                      : t("time_off:create_new_timeoff_request")
                  }
                  caption={
                    isTargetEmployeeLoading
                      ? "..."
                      : targetEmployee
                        ? renderEmployeeName(targetEmployee)
                        : ""
                  }
                />
                <Stack gap={"md"}>
                  <Stack gap={"smd"}>
                    <Title header={t("time_off:choose_category")} size="xs" />
                    <Box variant="border">
                      {employeeTimeOffCategoriesIsLoading && <Skeleton className="w-full h-10" />}
                      {employeeTimeOffCategories?.map((category: ApiEmployeeTimeOffCategory) => (
                        <Radio
                          key={category.id}
                          value={category.title}
                          checked={category.id === selectedCategoryId}
                          onChange={() => handleSelectCategory(category.id)}
                          title={category.title}
                          caption={
                            category.category_type !== ApiTimeOffCategoryType.flexible
                              ? t("time_off:available_on_date", {
                                  date: renderDate(periodStart ?? new Date()),
                                  balance: parseFloat(
                                    Number(category.balance.available_balance).toFixed(2)
                                  ),
                                })
                              : undefined
                          }
                        />
                      ))}
                    </Box>
                  </Stack>
                </Stack>
              </Stack>
              <Stack className={"card-panel"} gap={"2xl"}>
                <Title header={t("time_off:choose_period")} size="md" />
                <Stack gap={"sm"}>
                  <TimeOffPeriodPicker
                    key={`${periodStart?.getTime()}-${periodEnd?.getTime()}`}
                    dateFrom={periodStart}
                    dateTo={periodEnd}
                    onChange={(dateFrom, dateTo) => {
                      setValue("periodStart", dateFrom);
                      setValue("periodEnd", dateTo);
                    }}
                    label={t("time_off:choose_period")}
                    error={errors.periodStart?.message || errors.periodEnd?.message}
                    employeeId={targetEmployeeId}
                    excludeRequestId={timeOffRequest?.approval_request_details?.id}
                  />
                </Stack>
                {timeOffCheckIsLoading && (
                  <Stack gap={"sm"}>
                    <Skeleton className={"h-4 w-16"} />
                    <Skeleton className={"h-4 w-28"} />
                  </Stack>
                )}
                {periodStart &&
                  periodEnd &&
                  selectedCategory &&
                  (timeOffCheck || hasApprovalPolicyError) && (
                    <ListItem
                      onClick={hasApprovalPolicyError ? undefined : handleCustomizeBreakdown}
                      title={`${t("common:total")}: ${t(pluralKey, { count: timeOffDays })}`}
                      caption={
                        selectedCategory.category_type !== ApiTimeOffCategoryType.flexible &&
                        timeOffCheck?.possible_to_create
                          ? t("time_off:available_balance_on_dates", {
                              date: renderDate(periodStart),
                              balance: t(pluralKey, {
                                count: parseFloat(
                                  Number(selectedCategory.balance.available_balance).toFixed(2)
                                ),
                              }),
                              balance_forecast: t(pluralKey, {
                                count: parseFloat(Number(timeOffCheck.balance_left).toFixed(2)),
                              }),
                            })
                          : ""
                      }
                      captionSlot={
                        hasApprovalPolicyError ? (
                          <Text className={"text-secondary text-danger"}>
                            {t("time_off:contact_manager_to_check_approval_policy_settings")}
                          </Text>
                        ) : (
                          timeOffCheck &&
                          !timeOffCheck.possible_to_create &&
                          timeOffCheck.balance_left < 0 && (
                            <Text className={"text-secondary text-danger"}>
                              {t("time_off:insufficient_units_for_request", {
                                units: isHours
                                  ? t("time_off:hours_genitive").toLowerCase()
                                  : t("time_off:days_genitive").toLowerCase(),
                              })}
                            </Text>
                          )
                        )
                      }
                      valueSlot={
                        !hasApprovalPolicyError &&
                        timeOffCheck && (
                          <Stack className={"text-right"} direction={"vertical"} gap={"xs"}>
                            <LinkButtonTo
                              className={"whitespace-nowrap text-ellipsis overflow-hidden"}
                            >
                              {t("time_off:customize_breakdown")}
                            </LinkButtonTo>
                            {timeOffCheck.breakdown.is_manual_modified && (
                              <Text className={"text-secondary text-warning"}>
                                {t("time_off:modified")}
                              </Text>
                            )}
                          </Stack>
                        )
                      }
                    />
                  )}
              </Stack>
              <Stack className={"card-panel"} gap={"2xl"}>
                <Title header={t("time_off:add_note")} size="md" />
                <Textarea
                  label={t("time_off:add_note_description")}
                  {...register("note", { value: "" })}
                  error={errors.note?.message}
                />
              </Stack>
              <Stack className={"card-panel"} gap={"2xl"}>
                <Title header={t("common:add_files")} size="md" />
                <Stack gap={"md"}>
                  {timeOffCheck?.attachment_required && (
                    <Text className={"text-secondary"}>
                      {t("time_off:attachment_required_hint")}
                    </Text>
                  )}
                  {files && files.length > 0 && (
                    <Stack gap={"sm"}>
                      {files.map((file) => (
                        <FileItem
                          key={`file${file.id}`}
                          file={file}
                          onRemove={() => removeFile(file)}
                        />
                      ))}
                    </Stack>
                  )}
                  <Stack items={"start"}>
                    <Box>
                      <FileUpload
                        entityId={employeeMe?.id}
                        entityType={ApiEntityTypes.employee}
                        afterSend={(file) => {
                          if (file) setFiles([...files, file]);
                        }}
                        buttonText={t("common:attach")}
                      />
                    </Box>
                  </Stack>
                </Stack>
              </Stack>

              {(() => {
                if (!timeOffCheck && !hasApprovalPolicyError) return null;
                if (approvers && approvers.length > 0) {
                  return (
                    <Stack className={"card-panel"} gap={"2xl"}>
                      <Title header={t("time_off:approval")} size="md" />
                      <Stack gap={"sm"}>
                        <Stack direction={"horizontal"} gap={"sm"} className={"flex-wrap"}>
                          {approvers.map((approver) => (
                            <Tag key={`approver-${approver.id}`} variant={"dark"}>
                              {renderEmployeeName(approver)}
                            </Tag>
                          ))}
                        </Stack>
                      </Stack>
                    </Stack>
                  );
                } else if (
                  employeeMe?.plan.features.approvals &&
                  !(
                    timeOffCheck?.reject_code &&
                    timeOffCheck?.reject_code == ApiTimeOffLogFullRejectCode.no_approval_policy
                  ) &&
                  !hasApprovalPolicyError
                ) {
                  return (
                    <Stack className={"card-panel"} gap={"2xl"}>
                      <Title header={t("time_off:approval")} size="md" />
                      <Stack direction={"horizontal"} gap={"sm"} className={"flex-wrap"}>
                        <Text className={"text-secondary"}>
                          {t("approval:no_approvers_caption")}{" "}
                        </Text>
                      </Stack>
                    </Stack>
                  );
                }
                return null;
              })()}

              {canShowBypassOption && (
                <Stack className={"card-panel"} gap={"2xl"}>
                  <Title
                    header={
                      isEditMode ? t("approval:select_edit_as") : t("approval:select_create_as")
                    }
                    size={"md"}
                  />
                  <Box variant="border" className="p-4">
                    <Stack gap={"sm"}>
                      <Radio
                        caption={
                          isEditMode
                            ? t("approval:edit_as_employee_description")
                            : t("approval:create_as_employee_description")
                        }
                        checked={!(createAsAdmin || false)}
                        onChange={() => setValue("createAsAdmin", false)}
                        title={
                          isEditMode
                            ? t("approval:edit_as_employee")
                            : t("approval:create_as_employee")
                        }
                      />
                      <Radio
                        caption={
                          isEditMode
                            ? t("approval:edit_as_admin_description")
                            : t("approval:create_as_admin_description")
                        }
                        checked={createAsAdmin || false}
                        onChange={() => setValue("createAsAdmin", true)}
                        title={
                          isEditMode ? t("approval:edit_as_admin") : t("approval:create_as_admin")
                        }
                      />
                    </Stack>
                  </Box>
                </Stack>
              )}

              <Stack gap={"sm"}>
                {!timeOffCheck?.possible_to_create && timeOffCheck?.reject_code ? (
                  <TimeOffRequestRejectInfo code={timeOffCheck.reject_code} />
                ) : (
                  <ServerErrorField errors={errors} />
                )}
                <Stack gap={"md"}>
                  <Button
                    className={
                      timeOffCheck &&
                      !timeOffCheck.possible_to_create &&
                      !canIEdit(ApiPermission.approve_and_reject_leaves)
                        ? "opacity-50 pointer-events-none"
                        : ""
                    }
                    type={"submit"}
                    onClick={onSubmit}
                    size={"lg"}
                    disabled={isFormLoading || isAttachmentMissing}
                    isLoading={timeOffRequestUpdate.isPending || timeOffRequestCreate.isPending}
                  >
                    {isEditMode ? t("common:save") : t("common:submit_the_request")}
                  </Button>
                  {periodStart &&
                    periodEnd &&
                    selectedCategory &&
                    timeOffCheck &&
                    timeOffCheck.balance_left > 0 && (
                      <Stack>
                        <Title
                          header={`${t("common:total")}: ${t(pluralKey, {
                            count: timeOffDays,
                          })} (${selectedCategory.title})`}
                          size={"sm"}
                        />
                        <Text className={"text-secondary"}>
                          {`${renderDate(periodStart)} - ${renderDate(periodEnd)}, ${
                            isHours
                              ? t("plurals:hours_available_after_request", {
                                  count: timeOffCheck.balance_left,
                                })
                              : t("plurals:days_available_after_request", {
                                  count: timeOffCheck.balance_left,
                                })
                          }`}
                        </Text>
                      </Stack>
                    )}
                </Stack>
              </Stack>
            </Stack>
          </form>
        </FullScreenPage>
      )}
      {step == "final" && (
        <TimeOffRequestFinal
          timeOffLogFull={requestSaved}
          closePage={closePage}
          targetEmployeeId={targetEmployeeId}
        />
      )}
    </>
  );
};
